import { AuthContextValue, EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import {
    getUserTimezone,
    UserTimezoneReader,
} from "../../utils/date/userTimezone";
import { AgentInvokeParams, AgentModelChoice } from "../conversation";
import type {
    CreateCustomViewRequestBody,
    CreateCustomViewResponse,
    CustomViewTodoItemDTO,
    DeleteCustomViewResponse,
    GetCustomViewResponse,
    GetCustomViewsCountResponse,
    GetCustomViewsForUserResponse,
    GetCustomViewTodosResponse,
    GetFavoriteCustomViewsForUserResponse,
    PatchCustomViewRequestBody,
    PatchCustomViewResponse,
    SetFavoriteCustomViewsRequestBody,
    SetFavoriteCustomViewsResponse,
    UpdateNoteContentRequestBody,
    UpdateNoteContentResponse,
} from "../customView/types/customViewData.api";
import type {
    CustomView,
    CustomViewInput,
    CustomViewPatchParams,
    SmartLabelEntry,
    ViewFilter,
} from "../customView/types/customViews";
import type {
    GetSmartLabelResponseBody,
    GetSmartLabelsForUserResponseBody,
} from "../customView/types/smartLabel.api";
import type {
    AssignOrganizationAiModelRequest,
    OrganizationAiModelsResponse,
} from "../ia/types/aiModel.api";
import {
    AllLicenseDetailsResponse,
    LicensesResponse,
} from "../license/types/license.api";
import {
    AddLicensesToOrganizationRequest,
    AssignOrgLicenseToUserRequest,
    AssignOrgLicenseToUserResponse,
    CreateOrganizationRequestBody,
    DeleteOrganizationResponse,
    ListAllUsersAndLicenceResponse,
    ModifyOrgLicenseForB2BUserRequest,
    ModifyOrgLicenseForB2BUserResponse,
    OrganizationResponse,
    RemoveOrgLicenseFromB2BUserRequest,
    RemoveOrgLicenseFromB2BUserResponse,
    SetOrganizationLicenseExpirationDateRequest,
    UpdateOrganizationRequestBody,
    UpdateUserAdminRoleRequest,
    UpdateUserAdminRoleResponse,
} from "../license/types/teamData.api";
import {
    ACTIVE_SMART_LABELS,
    AttachmentPreviewRef,
    MailIds,
    SmartLabel,
    TodoItem,
} from "../mail/types/mail";
import type { EnrichedMail } from "../mail/types/mail.api";
import {
    BaseMail,
    ForwardMailRequestBody,
    ForwardMailResponseBody,
    GetAttachmentsPreviewsRequestBody,
    GetAttachmentsPreviewsResponseBody,
    GetMailsContentRequestBody,
    GetMailsContentResponseBody,
    GetMailsFromInternetMessageIdsResponseBody,
    GetMailsInAnyFolderResponseBody,
    QualifiedMail,
    SetMailFocusRequestBody,
    SetMailFocusResponseDTO,
    SetMailProcessedRequestBody,
    SetMailProcessedResponseDTO,
    SetMailSmartLabelRequestBody,
    SetMailSmartLabelResponseDTO,
    VerifyMailsExistenceResponseBody,
} from "../mail/types/mail.api";
import type { ScoringConfiguration } from "../mail/types/scoring";
import type {
    SttCorrectResponse,
    SttExtractFromEmailResponse,
    SttMailInput,
} from "../stt/types/stt";
import { UserProfile, UserSignature } from "../user";
import { UserContext } from "../user/types/context";
import {
    ApiFunctionalError,
    ApiNetworkError,
    ClientUpdateRequiredError,
    errorWithCause,
} from "./errors";
import {
    toDomainCustomView,
    toDomainSmartLabelEntry,
    toDtoCustomViewInput,
    toDtoPatchParams,
} from "./mappers/customView.mapper";
import { toDomainAcceptedTodo } from "./mappers/todo.mapper";
import {
    toDomainUserSignature,
    toDtoUserSignatureInput,
    toDtoUserSignatureUpdate,
} from "./mappers/userSignature.mapper";
import {
    AnalysisAgentResponseDTO,
    AssistantProjectDTO,
    AssistantProjectInputDTO,
    AssistantUsageDTO,
    BannerResponseDTO,
    BootstrapUserResponseDTO,
    ConversationAgentResponseDTO,
    ConversationAvis,
    ConversationAvisResponseDTO,
    ConversationCompacterResponseDTO,
    ConversationDetailDTO,
    ConversationEtapesMemoireDTO,
    ConversationEtapesProjetDTO,
    ConversationEtapesResponseDTO,
    ConversationEtapesSuiteDTO,
    ConversationFactDTO,
    ConversationSummaryDTO,
    ConversationTurnSaveDTO,
    CreateUserSignatureRequestBody,
    CreateUserSignatureResponseDTO,
    CustomViewAgentResponseDTO,
    DeleteUserSignatureResponseDTO,
    GetContactPhotoResponseDTO,
    GetUserSignatureByIdResponseDTO,
    GetUserSignaturesResponseDTO,
    InitializeContactRagResponseDTO,
    MailvistaResponseBody,
    ScoreMailResponseDTO,
    SearchContactsResponseDTO,
    SearchedMailEntryDTO,
    TodoItemResponseDTO,
    UpdateUserProfileRequestBody,
    UpdateUserProfileResponseBody,
    UpdateUserSignatureRequestBody,
    UpdateUserSignatureResponseDTO,
    WritingAgentResponseDTO,
} from "./types/apiResponse";
import { ClientInfo, formatClientHeader } from "./types/clientInfo";
import {
    API_CONFIG_v2,
    ApiVersion,
    RequestMethod,
    RestEndPoint,
    TARGET_API_V2,
} from "./types/const";

export class ApiManager {
    // #region Properties

    private readonly apiVersion: ApiVersion = ApiVersion.V1;

    private readonly eventBus: EventBus;

    private readonly userId: string;

    private readonly apiUrl: string;

    private readonly authContext: AuthContextValue;

    /**
     * Valeur de `X-Mv-Client` (F-1.24), calculée une fois. Absente quand l'app ne fournit pas
     * `clientInfo` : ni `X-Mv-Client` ni identifiant de tour ne partent alors (web et desktop
     * avant l'accord CORS de l'Api-Backend, BK-1.3).
     */
    private readonly clientHeader?: string;

    /**
     * Lecture du fuseau de l'appareil fournie par l'app (F-1.19 ; le mobile, par
     * `expo-localization`). Absente, le fuseau vient de `Intl` (le navigateur, sur le web).
     */
    private readonly readUserTimezone?: UserTimezoneReader;

    // #endregion Properties

    constructor(
        eventBus: EventBus,
        authContext: AuthContextValue,
        targetApi: TARGET_API_V2 = TARGET_API_V2.Api_Dev,
        clientInfo?: ClientInfo,
        readUserTimezone?: UserTimezoneReader,
    ) {
        logger.info(LOGS.ApiManager, "()");
        logger.info([LOGS.ApiManager], `targetApi : ${targetApi}`);

        this.eventBus = eventBus;

        this.authContext = authContext;
        this.userId = authContext.getUser().id;
        logger.info([LOGS.ApiManager], `userId     : ${this.userId}`);

        this.apiUrl = API_CONFIG_v2[targetApi];

        this.readUserTimezone = readUserTimezone;

        if (clientInfo) {
            this.clientHeader = formatClientHeader(clientInfo);
            logger.info(
                [LOGS.ApiManager],
                `X-Mv-Client : ${this.clientHeader}`,
            );
        }
    }

    // #region Public Methods

    // #region CustomViews

    /**
     * Crée une nouvelle customView (POST v1/customViews).
     */
    public async createCustomView(
        newCustomView: CustomViewInput,
    ): Promise<CustomView> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.CustomViews;
        const bodyContent: CreateCustomViewRequestBody =
            toDtoCustomViewInput(newCustomView);

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return toDomainCustomView(responseData as CreateCustomViewResponse);
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `CreateCustomView failed: ${error}`,
            );
            throw new Error(
                `Failed to create customView: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Supprime une customView (DELETE v1/customViews/:id).
     */
    public async deleteCustomView(
        customViewId: string,
    ): Promise<DeleteCustomViewResponse> {
        const requestMethod = RequestMethod.DELETE;
        const endPoint = RestEndPoint.CustomViewById;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${customViewId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
                { id: customViewId },
            );

            return responseData as DeleteCustomViewResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `DeleteCustomView failed: ${error}`,
            );
            throw new Error(
                `Failed to delete customView: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère une customView par son id (GET v1/customViews/:id).
     */
    public async getCustomView(customViewId: string): Promise<CustomView> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.CustomViewById;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${customViewId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
                { id: customViewId },
            );

            return toDomainCustomView(responseData as GetCustomViewResponse);
        } catch (error) {
            logger.error([LOGS.ApiManager], `getCustomView failed: ${error}`);
            throw new Error(
                `Failed to get customView: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère les todos des mails d'une customView (GET v1/customViews/:id/todos).
     */
    public async getCustomViewTodos(
        customViewId: string,
    ): Promise<CustomViewTodoItemDTO[]> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.CustomViewTodos;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${customViewId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
                { id: customViewId },
            );

            return responseData as GetCustomViewTodosResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getCustomViewTodos failed: ${error}`,
            );
            throw new Error(
                `Failed to get customView todos: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère le nombre de customViews existantes pour l'utilisateur (GET v1/customViews/count).
     */
    public async getCustomViewsCount(): Promise<number> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.CustomViewsCount;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
            );

            return (responseData as GetCustomViewsCountResponse).count;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `GetCustomViewsCount failed: ${error}`,
            );
            throw new Error(
                `Failed to get customView count: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère les customViews existantes pour l'utilisateur (GET v1/customViews).
     */
    public async getCustomViewsForUser(): Promise<CustomView[]> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.CustomViews;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
            );

            const dtos = responseData as GetCustomViewsForUserResponse;
            return dtos.map((dto) => toDomainCustomView(dto));
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `GetCustomViewsForUser failed: ${error}`,
            );
            throw new Error(
                `Failed to get customViews for user: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère les customViews favorites pour l'utilisateur (GET v1/customViews/favorites).
     */
    public async getFavoriteCustomViewsForUser(): Promise<CustomView[]> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.FavoriteCustomViews;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
            );

            return (responseData as GetFavoriteCustomViewsForUserResponse).map(
                (dto) => toDomainCustomView(dto),
            );
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `GetFavoriteCustomViewsForUser failed: ${error}`,
            );
            throw new Error(
                `Failed to get favorite customViews for user: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Modifie une customView existante (PATCH v1/customViews_v2/:id).
     *
     * La composition envoyée porte la date de chaque mail : c'est la clé d'ordre de la vue,
     * que l'API stocke et applique.
     */
    public async patchCustomView(
        customViewId: string,
        newCustomViewPatch: CustomViewPatchParams,
    ): Promise<CustomView> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.CustomViewV2ById;
        const bodyContent: PatchCustomViewRequestBody = toDtoPatchParams(
            customViewId,
            newCustomViewPatch,
        );

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${customViewId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
                { id: customViewId },
            );

            return toDomainCustomView(responseData as PatchCustomViewResponse);
        } catch (error) {
            logger.error([LOGS.ApiManager], `PatchCustomView failed: ${error}`);
            throw new Error(
                `Failed to patch customView: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Définit l'ordre des favoris (POST v1/customViews/favorites).
     * Retourne la liste des customViews favorites après mise à jour.
     */
    public async setFavoriteCustomViews(
        favoriteViewIds: string[],
    ): Promise<CustomView[]> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.FavoriteCustomViews;
        const bodyContent: SetFavoriteCustomViewsRequestBody = {
            favoriteViewIds,
        };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return (responseData as SetFavoriteCustomViewsResponse).map((dto) =>
                toDomainCustomView(dto),
            );
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `SetFavoriteCustomViews failed: ${error}`,
            );
            throw new Error(
                `Failed to set favorite customViews: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Met à jour le contenu de la note d'une customView (PATCH v1/customViews/:id/note).
     */
    public async updateNoteContent(
        customViewId: string,
        noteContent: string,
    ): Promise<CustomView> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.CustomViewSetNote;
        const bodyContent: UpdateNoteContentRequestBody = { noteContent };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${customViewId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
                { id: customViewId },
            );

            return toDomainCustomView(
                responseData as UpdateNoteContentResponse,
            );
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `UpdateNoteContent failed: ${error}`,
            );
            throw new Error(
                `Failed to update customView note: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion CustomViews

    // #region SmartLabels

    /**
     * Récupère les entries de tous les SmartLabels actifs de l'utilisateur (GET /v1/smartLabels).
     * Les entries sont pré-triées par le backend (Focus → score DESC, autres → sentDateTime DESC).
     * Ne pas re-trier côté SDK.
     */
    public async getSmartLabelsForUser(): Promise<
        Record<SmartLabel, SmartLabelEntry[]>
    > {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.SmartLabels;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
                undefined,
                "no-store",
            );

            const dto = responseData as GetSmartLabelsForUserResponseBody;

            const result = {} as Record<SmartLabel, SmartLabelEntry[]>;
            for (const label of ACTIVE_SMART_LABELS) {
                result[label] = (dto[label] ?? []).map((entry) =>
                    toDomainSmartLabelEntry(entry),
                );
            }
            return result;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getSmartLabelsForUser failed: ${error}`,
            );
            throw new Error(
                `Failed to get smartLabels for user: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère les entries d'un seul SmartLabel actif (GET /v1/smartLabels/:label).
     * Ne passer que des labels présents dans ACTIVE_SMART_LABELS (Suspect → 400 côté backend).
     */
    public async getSmartLabel(label: SmartLabel): Promise<SmartLabelEntry[]> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.SmartLabelByLabel;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — label: ${label}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
                { label },
            );

            const dtos = responseData as GetSmartLabelResponseBody;
            return dtos.map((entry) => toDomainSmartLabelEntry(entry));
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getSmartLabel(${label}) failed: ${error}`,
            );
            throw new Error(
                `Failed to get smartLabel ${label}: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion SmartLabels

    // #region Users Management

    // #region User profile

    /**
     * Initialise la session utilisateur via POST v1/me/bootstrap.
     *
     * Orchestre côté backend : résolution/création du profil DB, trial license,
     * customViews par défaut, mise à jour du lastLoginDate, et récupération des licences.
     * Retourne { userProfile, isNewUser, licenses }.
     */
    public async bootstrapUser(
        attemptNumber: number = 1,
    ): Promise<BootstrapUserResponseDTO> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.MeBootstrap;
        const urlParams = "";
        const bodyContent = {};

        logger.info(
            [LOGS.UserProfile],
            `Calling Api: ${endPoint} — tentative ${attemptNumber} — userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.UserProfile],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            logger.info(
                [LOGS.UserProfile],
                `bootstrap — tentative ${attemptNumber} réussie`,
            );
            return responseData as BootstrapUserResponseDTO;
        } catch (error) {
            logger.error(
                [LOGS.UserProfile],
                `bootstrap — tentative ${attemptNumber} échouée : ${error}`,
            );
            if (error instanceof ApiFunctionalError) {
                throw error;
            }
            throw new Error(
                `Failed to bootstrap user: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère le profil utilisateur depuis l'API backend (GET v1/me/userProfile).
     *
     * Le DTO backend (UserProfileResponseDTO) est structurellement identique
     * au type UserProfile du SDK — cast direct sans transformation.
     */
    public async getUserProfile(): Promise<UserProfile | null> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeUserProfile;
        const urlParams = "";

        logger.info(
            [LOGS.UserProfile],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.UserProfile],
                requestMethod,
                endPoint,
                urlParams,
            );

            if (!responseData) {
                return null;
            }

            return responseData as unknown as UserProfile;
        } catch (error) {
            logger.error([LOGS.UserProfile], `getUserProfile failed: ${error}`);
            throw new Error(
                `!!! Failed to get user profile from api: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Met à jour le profil utilisateur (PATCH v1/me/userProfile).
     */
    public async updateUserProfile(
        body: UpdateUserProfileRequestBody,
    ): Promise<boolean> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.MeUserProfile;
        const urlParams = "";
        const bodyContent = body;

        logger.info(
            [LOGS.UserProfile],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.UserProfile],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return (responseData as UpdateUserProfileResponseBody).success;
        } catch (error) {
            logger.error(
                [LOGS.UserProfile],
                `updateUserProfile failed: ${error}`,
            );
            return false;
        }
    }

    // #endregion User profile

    // #region Banners

    /**
     * Vérifie côté backend si le quota mensuel de mails analysés est atteint.
     * Si oui, retourne un BannerResponseDTO prêt à l'emploi. Si non, retourne null.
     *
     * Lève en cas d'échec : confondu avec `null`, il ferait effacer une bannière légitime.
     */
    public async getMaxAnalysedMailsBanner(): Promise<BannerResponseDTO | null> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeBannersMaxAnalysedMails;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info(
            [LOGS.BannerManager, LOGS.ApiManager],
            `Calling Api: ${endPoint}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.BannerManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData.banner as BannerResponseDTO;
        } catch (error) {
            logger.error(
                [LOGS.BannerManager, LOGS.ApiManager],
                `getMaxAnalysedMailsBanner failed: ${error}`,
            );
            throw error;
        }
    }

    /**
     * Vérifie côté backend si l'organisation de l'utilisateur est suspendue (HOLD).
     * Si oui, retourne un BannerResponseDTO avec le message approprié (admin vs user). Si non, retourne null.
     *
     * Lève en cas d'échec, comme {@link getMaxAnalysedMailsBanner}.
     */
    public async getOrganizationHoldBanner(): Promise<BannerResponseDTO | null> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeBannersOrganizationHold;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info(
            [LOGS.BannerManager, LOGS.ApiManager],
            `Calling Api: ${endPoint}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.BannerManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData.banner as BannerResponseDTO;
        } catch (error) {
            logger.error(
                [LOGS.BannerManager, LOGS.ApiManager],
                `getOrganizationHoldBanner failed: ${error}`,
            );
            throw error;
        }
    }

    // #endregion Banners

    // #region User context

    public async getUserContext(): Promise<UserContext | null> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeContext;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info(
            [LOGS.UserProfile],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        let responseData: any;
        try {
            responseData = await this.callApi(
                [LOGS.UserProfile],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const userContext = responseData as UserContext;

            return userContext;
        } catch (error) {
            console.error(error);
            throw new Error(`!!! Failed to call api  : ${error.message}`);
        }
    }

    // #endregion User context

    // #endregion Users Management

    // #region Mails Management

    // #region Mails

    public async GetMailIdsSince(
        dateToISOString: string,
        maxCount?: number,
    ): Promise<MailIds[]> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.GetMailIdsSince;
        // maxCount optionnel : onboarding => 300. Absent => défaut API (100), comportement polling inchangé.
        const urlParams =
            maxCount !== undefined
                ? `?date=${dateToISOString}&maxCount=${maxCount}`
                : `?date=${dateToISOString}`;
        const bodyContent = undefined;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        let responseData: any;
        try {
            responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const newMailsInternetMessageIds = responseData as MailIds[];

            return newMailsInternetMessageIds;
        } catch (error) {
            console.error(error);
            throw new Error(`!!! Failed to call api  : ${error.message}`);
        }
    }

    public async getMailsByInternetMessageIds(
        internetMessageIds: string[],
        inboxFolderId: string,
    ): Promise<GetMailsFromInternetMessageIdsResponseBody> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.GetMailsFromInternetMessageIds;
        const urlParams = "";
        // déc. #7 : inboxFolderId requis côté API (400 si absent/vide). Permet à l'API de construire
        // le MailboxFolderContext (sélection canonique stricte + témoin inbox-aware) sans appel Graph.
        const bodyContent = { internetMessageIds, inboxFolderId };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        let responseData: any;
        try {
            responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const mailsResponse =
                responseData as GetMailsFromInternetMessageIdsResponseBody;

            return mailsResponse;
        } catch (error) {
            console.error(error);
            throw new Error(`!!! Failed to call api  : ${error.message}`);
        }
    }

    /**
     * POST `v1/mails/getMailsInAnyFolderFromInternetMessageIds` : mêmes garanties de détection
     * de suppression que `getMailsByInternetMessageIds`, mais sans restriction de dossier.
     *
     * @param internetMessageIds - internetMessageIds de la page de vue à résoudre.
     * @param inboxFolderId      - reconstruit les mails analysés depuis leur occurrence inbox ; ne filtre rien.
     */
    public async getMailsInAnyFolderByInternetMessageIds(
        internetMessageIds: string[],
        inboxFolderId: string,
    ): Promise<GetMailsInAnyFolderResponseBody> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.GetMailsInAnyFolderFromInternetMessageIds;
        const urlParams = "";
        const bodyContent = { internetMessageIds, inboxFolderId };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as GetMailsInAnyFolderResponseBody;
        } catch (error) {
            console.error(error);
            throw new Error(`!!! Failed to call api  : ${error.message}`);
        }
    }

    /**
     * Vérification d'existence LÉGÈRE (verdicts-only, SANS contenu) des mails du store SmartLabel, pour
     * réconcilier les compteurs au démarrage sans télécharger des centaines de mails.
     *
     * Backend : route POST `v1/mails/verifyMailsExistence` (requiert un backend ≥ déploiement SDK 0.8.11).
     *
     * @param internetMessageIds - internetMessageIds à vérifier (typiquement tout le store SmartLabel).
     * @param inboxFolderId      - dossier Inbox courant (requis, déc. #7) : sélection canonique stricte
     *                             inbox-aware côté backend (gère le self-sent : copie Sent ≠ copie Inbox).
     * @returns `{ internetMessageIdsToRemove }` : ids confirmés absents / sans copie inbox → à retirer du store.
     * @throws {Error} si l'appel API échoue (best-effort attendu côté appelant — cf. `_reconcileSmartLabelStoreCounters`).
     */
    public async verifyMailsExistence(
        internetMessageIds: string[],
        inboxFolderId: string,
    ): Promise<VerifyMailsExistenceResponseBody> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.VerifyMailsExistence;
        const urlParams = "";
        const bodyContent = { internetMessageIds, inboxFolderId };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as VerifyMailsExistenceResponseBody;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `verifyMailsExistence failed: ${error}`,
            );
            throw new Error(
                `!!! Failed to call verifyMailsExistence: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async scoringMail(
        mail: EnrichedMail,
        scoringConfiguration: ScoringConfiguration,
    ): Promise<ScoreMailResponseDTO> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.ScoreMail;
        const urlParams = "";
        const bodyContent = { mail, scoringConfiguration };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as ScoreMailResponseDTO;
        } catch (error) {
            logger.error([LOGS.ApiManager], `callScoreMail failed: ${error}`);
            throw new Error(
                `!!! Failed to call scoreMail: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async analyseAndSaveMail(
        mailToSave: BaseMail,
        inboxFolderId: string,
    ): Promise<QualifiedMail> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.AnalysisAndSaveMail;
        const urlParams = "";
        // déc. #7 : l'API exige inboxFolderId (400 si absent) pour construire le MailboxFolderContext
        // (analyse inbox-aware). Déploiement conjoint OBLIGATOIRE avec le Backend.
        const bodyContent = { ...mailToSave, inboxFolderId };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        let responseData: any;
        try {
            responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const mailsResponse = responseData.qualifiedMail as QualifiedMail;

            if (this.eventBus) {
                this.eventBus.emit("ia-token-spent", responseData.spentTokens);
            }

            return mailsResponse;
        } catch (error) {
            console.error(error);
            throw new Error(`!!! Failed to call api  : ${error.message}`);
        }
    }

    /**
     * Déplace manuellement un mail vers un autre SmartLabel (PATCH /v1/mails/smart-label).
     * WS6c / déc. #8 — `uniqueMailvistaId` dans le BODY (route statique ; bug encodage IIS sur le path).
     * Le Backend dérive l'imid au bord hexa et résout la copie canonique inbox.
     * Retourne le document mail mis à jour après application des règles métier backend.
     */
    public async setMailSmartLabel(
        uniqueMailvistaId: string,
        smartLabel: SmartLabel,
    ): Promise<SetMailSmartLabelResponseDTO> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.MailSetSmartLabel;
        const bodyContent: SetMailSmartLabelRequestBody = {
            uniqueMailvistaId,
            smartLabel,
            isUserEdited: true,
        };

        logger.info(
            [LOGS.MailManager, LOGS.ApiManager],
            `Calling Api: ${endPoint} — uniqueMailvistaId: ${uniqueMailvistaId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return responseData as SetMailSmartLabelResponseDTO;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `SetMailSmartLabel failed: ${error}`,
            );
            throw new Error(
                `Failed to set mail smart label: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // WS6c / déc. #8 — `uniqueMailvistaId` dans le BODY (PATCH /v1/mails/focus ; route statique, bug encodage IIS). Backend dérive l'imid → copie canonique inbox.
    public async setMailFocus(
        uniqueMailvistaId: string,
        isFocus: boolean,
    ): Promise<SetMailFocusResponseDTO> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.MailSetFocus;
        const bodyContent: SetMailFocusRequestBody = {
            uniqueMailvistaId,
            isFocus,
        };

        logger.info(
            [LOGS.MailManager, LOGS.ApiManager],
            `Calling Api: ${endPoint} — uniqueMailvistaId: ${uniqueMailvistaId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return responseData as SetMailFocusResponseDTO;
        } catch (error) {
            logger.error([LOGS.ApiManager], `SetMailFocus failed: ${error}`);
            throw new Error(
                `Failed to set mail focus: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // WS6c / déc. #8 — `uniqueMailvistaId` dans le BODY (PATCH /v1/mails/processed ; route statique, bug encodage IIS). Backend dérive l'imid → copie canonique inbox.
    public async setMailProcessed(
        uniqueMailvistaId: string,
        isUserProcessed: boolean,
    ): Promise<SetMailProcessedResponseDTO> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.MailSetProcessed;
        const bodyContent: SetMailProcessedRequestBody = {
            uniqueMailvistaId,
            isUserProcessed,
        };

        logger.info(
            [LOGS.MailManager, LOGS.ApiManager],
            `Calling Api: ${endPoint} — uniqueMailvistaId: ${uniqueMailvistaId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return responseData as SetMailProcessedResponseDTO;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `SetMailProcessed failed: ${error}`,
            );
            throw new Error(
                `Failed to set mail processed: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Dernier `sentDateTime` (ISO) analysé en DB pour l'utilisateur, ou '' si aucun.
     * Remplace l'ancien `Email_GetLastProcessedMailSentDate` (Core).
     */
    public async getLastProcessedMailSentDate(): Promise<string> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.GetLastProcessedMailSentDate;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
            );
            return (responseData?.lastProcessedMailSentDate ?? "") as string;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getLastProcessedMailSentDate failed: ${error}`,
            );
            throw new Error(
                `Failed to get last processed mail sent date: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Mails analysés filtrés par catégories / statut / keywords, avec leur date.
     * Remplace l'ancien `Email_SearchMailsByCategoriesAndStatus` (Core).
     */
    public async searchMailsByCategoriesAndStatus(
        categories: string[],
        mailStatus: string[],
        keywords: string[],
        sortOrder: string,
        limit: number | undefined,
    ): Promise<SearchedMailEntryDTO[]> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.SearchMailsByCategoriesStatus;
        const bodyContent = {
            categories,
            mailStatus,
            keywords,
            sortOrder,
            limit: limit ?? null,
        };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );
            return (responseData?.mails ?? []) as SearchedMailEntryDTO[];
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `searchMailsByCategoriesAndStatus failed: ${error}`,
            );
            throw new Error(
                `Failed to search mails by categories/status: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Purge en DB les mails analysés correspondant aux internetMessageIds donnés.
     * Retourne le nombre supprimé. Remplace/unifie les anciens Core
     * `Email_DeleteMail` + `Email_GetMailIdByInternetMessageIds` + `Email_DeleteObsoleteMailsByInternetMessageIds`.
     */
    public async deleteAnalyzedMailsByInternetMessageIds(
        internetMessageIds: string[],
    ): Promise<number> {
        if (internetMessageIds.length === 0) return 0;

        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.DeleteAnalyzedMails;
        const bodyContent = { internetMessageIds };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );
            return (responseData?.deletedCount ?? 0) as number;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `deleteAnalyzedMailsByInternetMessageIds failed: ${error}`,
            );
            throw new Error(
                `Failed to delete analyzed mails: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère le contenu « widgets » (attachments/links/events) d'un lot de mails
     * (POST v1/mails/content). Données brutes par mail : la dédup cross-mail et le filtrage des events
     * passés sont faits au read côté SDK. Alimente le cache par-mail (Record MailManager). Pass-through.
     */
    public async getMailsViewContentByInternetMessageIds(
        internetMessageIds: string[],
    ): Promise<GetMailsContentResponseBody> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.MailContent;
        const bodyContent: GetMailsContentRequestBody = { internetMessageIds };

        logger.info(
            [LOGS.MailManager, LOGS.ApiManager],
            `Calling Api: ${endPoint} — ${internetMessageIds.length} mail(s)`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return responseData as GetMailsContentResponseBody;
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.ApiManager],
                "getMailsViewContentByInternetMessageIds failed",
                error,
            );
            throw new Error(
                `Failed to get mails content: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async forwardMail(
        payload: ForwardMailRequestBody,
    ): Promise<ForwardMailResponseBody> {
        const endPoint = RestEndPoint.MailForward;

        logger.info(
            [LOGS.MailManager, LOGS.ApiManager],
            `Calling Api: ${endPoint} — forwardId ${payload.forwardId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                RequestMethod.POST,
                endPoint,
                "",
                payload,
            );

            return responseData as ForwardMailResponseBody;
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.ApiManager],
                "forwardMail failed",
                error,
            );
            throw error;
        }
    }

    /**
     * Récupère les previews (data URL base64) d'un lot de pièces jointes à la demande
     * (POST v1/mails/attachments/previews). Les previews sont trop lourdes pour être servies par défaut
     * (ex. métadonnées d'une CustomView) → demandées séparément et en batch pour limiter les allers-retours.
     * Le Backend résout le graphId courant à partir de l'uniqueMailvistaId (identité stable). Pass-through.
     */
    public async getAttachmentsPreviews(
        refs: AttachmentPreviewRef[],
    ): Promise<GetAttachmentsPreviewsResponseBody> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.MailAttachmentsPreviews;
        const bodyContent: GetAttachmentsPreviewsRequestBody = {
            attachments: refs,
        };

        logger.info(
            [LOGS.MailManager, LOGS.ApiManager],
            `Calling Api: ${endPoint} — ${refs.length} attachment(s)`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.MailManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return responseData as GetAttachmentsPreviewsResponseBody;
        } catch (error) {
            logger.error(
                [LOGS.MailManager, LOGS.ApiManager],
                "getAttachmentsPreviews failed",
                error,
            );
            throw new Error(
                `Failed to get attachments previews: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion Mails

    // #endregion Mails Management

    // #region Licenses

    // #region User Licenses
    public async fetchLicensesDetails(): Promise<LicensesResponse> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeLicenses;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} for userId: ${this.userId}`,
        );

        let responseData: any;
        try {
            responseData = await this.callApi(
                [LOGS.LicenseManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const licensesResponse = responseData as LicensesResponse;
            return licensesResponse;
        } catch (error) {
            console.error(error);
            throw new Error(`!!! Failed to call api  : ${error.message}`);
        }
    }

    // #endregion

    // #region LicenseDetails
    public async getAllLicenseDetails(): Promise<AllLicenseDetailsResponse> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.LicensesDetails;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        let responseData: any;
        try {
            responseData = await this.callApi(
                [LOGS.LicenseManager, LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const allLicenseDetailsResponse =
                responseData as AllLicenseDetailsResponse;
            return allLicenseDetailsResponse;
        } catch (error) {
            console.error(error);
            throw new Error(`!!! Failed to call api  : ${error.message}`);
        }
    }

    // #endregion LicenseDetails

    // #endregion Licenses

    // #region Organizations

    /**
     * Crée une nouvelle organisation (POST v1/organizations).
     */
    public async createOrganization(
        body: CreateOrganizationRequestBody,
    ): Promise<OrganizationResponse> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.Organizations;
        const urlParams = "";
        const bodyContent = body;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as OrganizationResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `createOrganization failed: ${error}`,
            );
            throw new Error(
                `Failed to create organization: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère une organisation par son id (GET v1/organizations/:id).
     */
    public async getOrganization(
        organizationId: string,
    ): Promise<OrganizationResponse> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.OrganizationById;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as OrganizationResponse;
        } catch (error) {
            logger.error([LOGS.ApiManager], `getOrganization failed: ${error}`);
            throw new Error(
                `Failed to get organization: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Met à jour une organisation (PATCH v1/organizations/:id).
     */
    public async updateOrganization(
        organizationId: string,
        body: UpdateOrganizationRequestBody,
    ): Promise<OrganizationResponse> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.OrganizationById;
        const urlParams = "";
        const bodyContent = body;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as OrganizationResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `updateOrganization failed: ${error}`,
            );
            throw new Error(
                `Failed to update organization: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Supprime une organisation (DELETE v1/organizations/:id).
     */
    public async deleteOrganization(
        organizationId: string,
    ): Promise<DeleteOrganizationResponse> {
        const requestMethod = RequestMethod.DELETE;
        const endPoint = RestEndPoint.OrganizationById;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as DeleteOrganizationResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `deleteOrganization failed: ${error}`,
            );
            throw new Error(
                `Failed to delete organization: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Ajoute des licences à une organisation (PATCH v1/organizations/:id/licenses).
     */
    public async addLicensesToOrganization(
        organizationId: string,
        licenses: AddLicensesToOrganizationRequest[],
    ): Promise<OrganizationResponse> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.OrganizationLicenses;
        const urlParams = "";
        const bodyContent = licenses;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as OrganizationResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `addLicensesToOrganization failed: ${error}`,
            );
            throw new Error(
                `Failed to add licenses to organization: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Définit la date d'expiration d'une licence d'organisation
     * (PATCH v1/organizations/:id/licenses/:orgLicenseId/expiration-date).
     */
    public async setOrganizationLicenseExpirationDate(
        organizationId: string,
        orgLicenseId: string,
        body: SetOrganizationLicenseExpirationDateRequest,
    ): Promise<OrganizationResponse> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.OrganizationLicenseExpirationDate;
        const urlParams = "";
        const bodyContent = body;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}, orgLicenseId: ${orgLicenseId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId, orgLicenseId },
            );

            return responseData as OrganizationResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `setOrganizationLicenseExpirationDate failed: ${error}`,
            );
            throw new Error(
                `Failed to set organization license expiration date: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Liste tous les utilisateurs et leurs licences pour une organisation
     * (GET v1/organizations/:id/users).
     */
    public async listAllUsersAndLicence(
        organizationId: string,
    ): Promise<ListAllUsersAndLicenceResponse> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.OrganizationUsers;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as ListAllUsersAndLicenceResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `listAllUsersAndLicence failed: ${error}`,
            );
            throw new Error(
                `Failed to list users and licences: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Affecte un utilisateur B2B à un slot de licence d'organisation
     * (POST v1/organizations/:id/assign-b2b-user).
     *
     * @param organizationId - Id de l'organisation
     * @param body           - { userId, userMail, orgLicenseId }
     */
    public async assignOrganizationLicenseToUser(
        organizationId: string,
        body: AssignOrgLicenseToUserRequest,
    ): Promise<AssignOrgLicenseToUserResponse> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.OrganizationAssignB2BUser;
        const urlParams = "";
        const bodyContent = body;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as AssignOrgLicenseToUserResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `assignOrganizationLicenseToUser failed: ${error}`,
            );
            throw new Error(
                `Failed to assign B2B user to organization license: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Désaffecte un utilisateur B2B d'un slot de licence d'organisation
     * (POST v1/organizations/:id/unassign-b2b-user).
     *
     * @param organizationId - Id de l'organisation
     * @param body           - { userId, userMail, orgLicenseId }
     */
    public async removeOrganizationLicenseFromB2BUser(
        organizationId: string,
        body: RemoveOrgLicenseFromB2BUserRequest,
    ): Promise<RemoveOrgLicenseFromB2BUserResponse> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.OrganizationUnassignB2BUser;
        const urlParams = "";
        const bodyContent = body;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as RemoveOrgLicenseFromB2BUserResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `removeOrganizationLicenseFromB2BUser failed: ${error}`,
            );
            throw new Error(
                `Failed to remove B2B user from organization license: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Réaffecte un utilisateur B2B vers un autre slot de licence d'organisation
     * (POST v1/organizations/:id/reassign-b2b-user).
     *
     * @param organizationId - Id de l'organisation
     * @param body           - { userId, userMail, newOrgLicenseId }
     */
    public async modifyOrganizationLicenseForB2BUser(
        organizationId: string,
        body: ModifyOrgLicenseForB2BUserRequest,
    ): Promise<ModifyOrgLicenseForB2BUserResponse> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.OrganizationReassignB2BUser;
        const urlParams = "";
        const bodyContent = body;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as ModifyOrgLicenseForB2BUserResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `modifyOrganizationLicenseForB2BUser failed: ${error}`,
            );
            throw new Error(
                `Failed to modify B2B user organization license: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Updates the admin role of a user within an organization
     * (POST v1/organizations/:id/set-admin-role).
     *
     * @param organizationId - Id of the organization
     * @param body           - { userId, userMail, setAdmin }
     */
    public async updateUserAdminRole(
        organizationId: string,
        body: UpdateUserAdminRoleRequest,
    ): Promise<UpdateUserAdminRoleResponse> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.OrganizationSetAdminRole;
        const urlParams = "";
        const bodyContent = body;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — organizationId: ${organizationId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                { id: organizationId },
            );

            return responseData as UpdateUserAdminRoleResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `updateUserAdminRole failed: ${error}`,
            );
            throw new Error(
                `Failed to update user admin role: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion Organizations

    // #region AI Agents

    public async callWritingAgent(
        currentAnswer: string,
        previousDiscussions: string,
        speechOption: string,
        currentAnswerToReplace: string,
    ): Promise<string> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.AiWritingAgent;
        const urlParams = "";
        const bodyContent = {
            currentAnswer,
            previousDiscussions,
            speechOption,
            currentAnswerToReplace,
        };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const writingResponse = responseData as WritingAgentResponseDTO;

            if (this.eventBus) {
                this.eventBus.emit(
                    "ia-token-spent",
                    writingResponse.spentTokens,
                );
            }

            return writingResponse.replaceContent;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `callWritingAgent failed: ${error}`,
            );
            throw new Error(
                `!!! Failed to call writing agent: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async callSttCorrect(
        text: string,
        contextTags?: string,
    ): Promise<SttCorrectResponse> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.SttCorrect;
        const urlParams = "";
        const bodyContent: Record<string, string> = { text };
        if (contextTags) bodyContent["context_tags"] = contextTags;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as SttCorrectResponse;
        } catch (error) {
            logger.error([LOGS.ApiManager], `callSttCorrect failed: ${error}`);
            throw new Error(
                `!!! Failed to call STT correct: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async callExtractFromEmail(
        mail: SttMailInput,
    ): Promise<SttExtractFromEmailResponse> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.SttExtractFromEmail;
        const urlParams = "";
        const bodyContent = {
            mail: {
                subject: mail.subject,
                body: mail.body,
                from_name: mail.fromName,
                from_email: mail.fromEmail,
                recipients: mail.recipients,
            },
        };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as SttExtractFromEmailResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `callExtractFromEmail failed: ${error}`,
            );
            throw new Error(
                `!!! Failed to call STT extract from email: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async callAnalysisAgent(
        agentInvokeParams: AgentInvokeParams,
    ): Promise<string> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.AiAnalysisAgent;
        const urlParams = "";
        const bodyContent = {
            type: agentInvokeParams.type,
            instructions: agentInvokeParams.instructions,
            contentData: agentInvokeParams.contentData,
            userHistory: agentInvokeParams.userHistory,
        };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — sessionId: ${agentInvokeParams.sessionId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const analysisResponse = responseData as AnalysisAgentResponseDTO;

            if (this.eventBus) {
                this.eventBus.emit(
                    "ia-token-spent",
                    analysisResponse.spentTokens,
                );
            }

            return analysisResponse.analysisResult;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `callAnalysisAgent failed: ${error}`,
            );
            throw new Error(
                `!!! Failed to call analysis agent: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * @param options.traceId - identifiant du tour de l'assistant, envoyé en `X-Mv-Trace-Id` quand
     * l'app a fourni `clientInfo` (F-1.24).
     */
    public async callConversationAgent(
        messages: Array<{ role: string; content: string }>,
        options?: { traceId?: string },
    ): Promise<ConversationAgentResponseDTO> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.AiConversationAgent;
        const urlParams = "";
        const bodyContent = { messages, ...this._userTimezoneField() };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                undefined,
                undefined,
                options?.traceId,
            );

            const conversationResponse =
                responseData as ConversationAgentResponseDTO;

            if (this.eventBus) {
                this.eventBus.emit(
                    "ia-token-spent",
                    conversationResponse.spentTokens,
                );
            }

            return conversationResponse;
        } catch (error) {
            this._rethrowAssistantError(
                error,
                "callConversationAgent",
                "call conversation agent",
            );
        }
    }

    /**
     * Un appel du contrat par étapes (docs/contrat-etapes.md de l'Api-Ai) : `{ messages,
     * userTimezone }`, plus `suite` à partir du deuxième appel du tour. Avec `memoire` : les échanges
     * de la conversation à chaque appel, et ses faits retenus. En-têtes et erreurs comme `callConversationAgent`.
     * @param options.traceId - identifiant du tour, le même pour tous ses appels, envoyé en
     * `X-Mv-Trace-Id` quand l'app a fourni `clientInfo` (F-1.24).
     * @param options.modele - le modèle choisi par l'utilisateur, envoyé seulement s'il est forcé
     * (« small », « medium ») ; « auto » : rien, l'Api-Ai décide.
     * @param options.conversationId - la conversation du tour : avec l'identifiant du tour, l'Api-Backend
     * y range la consommation de l'appel (il ne relaie ni l'un ni l'autre à l'Api-Ai).
     */
    public async callConversationEtapes(
        messages: Array<{ role: string; content: string }>,
        suite?: ConversationEtapesSuiteDTO,
        options?: {
            traceId?: string;
            signal?: AbortSignal;
            modele?: AgentModelChoice;
            projet?: ConversationEtapesProjetDTO;
            conversationId?: string;
        },
        memoire?: ConversationEtapesMemoireDTO,
    ): Promise<ConversationEtapesResponseDTO> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.AiConversationEtapes;
        const urlParams = "";
        const bodyContent = {
            messages,
            ...this._userTimezoneField(),
            ...(suite ? { suite } : {}),
            ...(memoire ? { echanges: memoire.echanges } : {}),
            ...(memoire?.faits ? { faits: memoire.faits } : {}),
            ...(options?.modele && options.modele !== "auto"
                ? { modele: options.modele }
                : {}),
            ...(options?.projet ? { projet: options.projet } : {}),
            ...this._usageFields(options),
        };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
                undefined,
                undefined,
                options?.traceId,
                options?.signal,
            );

            const etapesResponse =
                responseData as ConversationEtapesResponseDTO;

            this._emitSpentTokens(etapesResponse.spentTokens);

            return etapesResponse;
        } catch (error) {
            this._rethrowAssistantError(
                error,
                "callConversationEtapes",
                "call conversation etapes",
            );
        }
    }

    // #region Historique des conversations (contrat par étapes)

    /** Garde un tour (ou un compactage) dans l'historique de la conversation. */
    public async callConversationTurnSave(
        turn: ConversationTurnSaveDTO,
    ): Promise<{ saved: boolean; position: number }> {
        return this.callConversationHistory(
            RequestMethod.POST,
            RestEndPoint.AiConversationTurns,
            "save turn",
            turn,
        );
    }

    /**
     * Les conversations de l'historique, la plus récente d'abord ; avec `query`, celles où ce mot clé
     * apparaît (titre, questions, réponses), chacune avec un extrait.
     */
    public async callConversationsList(
        query?: string,
    ): Promise<ConversationSummaryDTO[]> {
        const response = (await this.callConversationHistory(
            RequestMethod.GET,
            RestEndPoint.AiConversations,
            "list",
            undefined,
            undefined,
            query ? `?q=${encodeURIComponent(query)}` : "",
        )) as { conversations?: ConversationSummaryDTO[] };
        return response?.conversations ?? [];
    }

    /** Une conversation de l'historique, avec ses tours. */
    public async callConversationGet(
        id: string,
    ): Promise<ConversationDetailDTO> {
        return this.callConversationHistory(
            RequestMethod.GET,
            RestEndPoint.AiConversationById,
            "get",
            undefined,
            { id },
        );
    }

    public async callConversationRename(
        id: string,
        title: string,
    ): Promise<{ id: string; title: string }> {
        return this.callConversationHistory(
            RequestMethod.PATCH,
            RestEndPoint.AiConversationById,
            "rename",
            { title },
            { id },
        );
    }

    public async callConversationDelete(id: string): Promise<void> {
        await this.callConversationHistory(
            RequestMethod.DELETE,
            RestEndPoint.AiConversationById,
            "delete",
            undefined,
            { id },
        );
    }

    public async callConversationsDeleteAll(): Promise<void> {
        await this.callConversationHistory(
            RequestMethod.DELETE,
            RestEndPoint.AiConversations,
            "delete all",
        );
    }

    // #region Projets de l'assistant

    /** Les projets de l'utilisateur, le plus récemment modifié d'abord, avec leur nombre de conversations. */
    public async callProjectsList(): Promise<AssistantProjectDTO[]> {
        const response = (await this.callConversationHistory(
            RequestMethod.GET,
            RestEndPoint.AiProjects,
            "list projects",
        )) as { projects?: AssistantProjectDTO[] };
        return response?.projects ?? [];
    }

    /**
     * Ce que l'assistant a consommé : l'utilisateur depuis le début du mois, et la conversation
     * `conversationId` depuis son premier tour.
     */
    public async callAssistantUsage(
        conversationId: string,
    ): Promise<AssistantUsageDTO> {
        return this.callConversationHistory(
            RequestMethod.GET,
            RestEndPoint.AiAssistantUsage,
            "read assistant usage",
            undefined,
            undefined,
            `?conversationId=${encodeURIComponent(conversationId)}`,
        );
    }

    public async callProjectCreate(
        project: AssistantProjectInputDTO,
    ): Promise<AssistantProjectDTO> {
        return this.callConversationHistory(
            RequestMethod.POST,
            RestEndPoint.AiProjects,
            "create project",
            project,
        );
    }

    /** Modifie les seuls champs donnés ; `instructions: null` retire les consignes. */
    public async callProjectUpdate(
        id: string,
        changes: Partial<AssistantProjectInputDTO>,
    ): Promise<AssistantProjectDTO> {
        return this.callConversationHistory(
            RequestMethod.PATCH,
            RestEndPoint.AiProjectById,
            "update project",
            changes,
            { id },
        );
    }

    /** Supprime le projet ; ses conversations reviennent à la liste générale. */
    public async callProjectDelete(id: string): Promise<void> {
        await this.callConversationHistory(
            RequestMethod.DELETE,
            RestEndPoint.AiProjectById,
            "delete project",
            undefined,
            { id },
        );
    }

    /** Range une conversation dans un projet, ou l'en sort (`projectId` `null`). */
    public async callConversationSetProject(
        id: string,
        projectId: string | null,
    ): Promise<{ id: string; projectId: string | null }> {
        return this.callConversationHistory(
            RequestMethod.PUT,
            RestEndPoint.AiConversationProject,
            "set project",
            { projectId },
            { id },
        );
    }

    /** Remplace les faits retenus dans une conversation de l'historique (liste vide : plus aucun). */
    public async callConversationSetMemory(
        id: string,
        faits: ConversationFactDTO[],
    ): Promise<{ id: string; faits: ConversationFactDTO[] }> {
        return this.callConversationHistory(
            RequestMethod.PUT,
            RestEndPoint.AiConversationMemory,
            "set memory",
            { faits },
            { id },
        );
    }

    // #endregion

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private async callConversationHistory<T = any>(
        method: RequestMethod,
        endPoint: RestEndPoint,
        what: string,
        body?: unknown,
        pathParams?: Record<string, string>,
        urlParams = "",
    ): Promise<T> {
        logger.info([LOGS.ApiManager], `Calling Api: ${method} ${endPoint}`);
        try {
            return (await this.callApi(
                [LOGS.ApiManager],
                method,
                endPoint,
                urlParams,
                body,
                pathParams,
            )) as T;
        } catch (error) {
            this._rethrowAssistantError(
                error,
                `conversation history (${what})`,
                `${what} in the conversation history`,
            );
        }
    }

    // #endregion

    /**
     * Pouce sous une réponse (contrat par étapes) : l'identifiant du tour et l'avis, rien d'autre ;
     * l'Api-Ai l'écrit à son journal. En-têtes et erreurs comme `callConversationCompacter`.
     */
    public async callConversationAvis(
        traceId: string,
        avis: ConversationAvis,
    ): Promise<ConversationAvisResponseDTO> {
        const endPoint = RestEndPoint.AiConversationAvis;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            return (await this.callApi(
                [LOGS.ApiManager],
                RequestMethod.POST,
                endPoint,
                "",
                { traceId, avis },
            )) as ConversationAvisResponseDTO;
        } catch (error) {
            this._rethrowAssistantError(
                error,
                "callConversationAvis",
                "send the answer feedback",
            );
        }
    }

    /**
     * Compacter la conversation (contrat par étapes, bouton « Compacter ») : l'Api-Ai résume ses
     * échanges en un seul. En-têtes et erreurs comme `callConversationEtapes`.
     * @param options.conversationId - la conversation compactée : avec l'identifiant de l'appel,
     * l'Api-Backend y range sa consommation (il ne relaie ni l'un ni l'autre à l'Api-Ai).
     */
    public async callConversationCompacter(
        memoire: ConversationEtapesMemoireDTO,
        options?: { traceId?: string; conversationId?: string },
    ): Promise<ConversationCompacterResponseDTO> {
        const endPoint = RestEndPoint.AiConversationCompacter;
        const bodyContent = {
            echanges: memoire.echanges,
            ...(memoire.faits ? { faits: memoire.faits } : {}),
            ...this._userTimezoneField(),
            ...this._usageFields(options),
        };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const response = (await this.callApi(
                [LOGS.ApiManager],
                RequestMethod.POST,
                endPoint,
                "",
                bodyContent,
                undefined,
                undefined,
                options?.traceId,
            )) as ConversationCompacterResponseDTO;

            this._emitSpentTokens(response.spentTokens);
            return response;
        } catch (error) {
            this._rethrowAssistantError(
                error,
                "callConversationCompacter",
                "compact the conversation",
            );
        }
    }

    /**
     * Échec d'un appel de l'assistant : journalisé (`<label> failed: …`), puis relancé.
     * Mise à jour requise (F-1.25) : relancée telle quelle, l'app sait l'afficher. Sinon, l'erreur
     * d'origine reste en `cause` : le ConversationManager y lit la cause du tour raté (quota, réseau,
     * service — F-1.17).
     */
    private _rethrowAssistantError(
        error: unknown,
        label: string,
        failure: string,
    ): never {
        logger.error([LOGS.ApiManager], `${label} failed: ${error}`);
        if (error instanceof ClientUpdateRequiredError) {
            throw error;
        }
        throw errorWithCause(
            `!!! Failed to ${failure}: ${error instanceof Error ? error.message : String(error)}`,
            error,
        );
    }

    /** Jetons consommés par un appel de l'assistant, émis seulement si l'Api-Ai les donne. */
    private _emitSpentTokens(spentTokens: unknown): void {
        if (this.eventBus && typeof spentTokens === "number") {
            this.eventBus.emit("ia-token-spent", spentTokens);
        }
    }

    public async callCustomViewAgent(userPrompt: string): Promise<ViewFilter> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.AiViewFilterAgent;
        const urlParams = "";
        const bodyContent = { userPrompt, ...this._userTimezoneField() };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            const customViewResponse =
                responseData as CustomViewAgentResponseDTO;

            if (this.eventBus) {
                this.eventBus.emit(
                    "ia-token-spent",
                    customViewResponse.spentTokens,
                );
            }

            return customViewResponse.viewFilter;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `callCustomViewAgent failed: ${error}`,
            );
            throw new Error(
                `!!! Failed to call custom view agent: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion AI Agents

    // #region Organization AI Model

    /**
     * Récupère le catalogue des modèles d'IA disponibles pour l'organisation
     * ainsi que le modèle actuellement sélectionné.
     * GET `v1/me/organization/ai-model`.
     */
    public async getOrganizationAiModel(): Promise<OrganizationAiModelsResponse> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeOrganizationAiModel;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as OrganizationAiModelsResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getOrganizationAiModel failed: ${error}`,
            );
            throw new Error(
                `!!! Failed to get organization AI model: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Définit le modèle d'IA actif pour l'organisation.
     * Renvoie le même payload que `getOrganizationAiModel` (catalogue + modèle sélectionné).
     * PATCH `v1/me/organization/ai-model`.
     */
    public async assignOrganizationAiModel(
        modelId: string,
    ): Promise<OrganizationAiModelsResponse> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.MeOrganizationAiModel;
        const urlParams = "";
        const bodyContent: AssignOrganizationAiModelRequest = { modelId };

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as OrganizationAiModelsResponse;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `assignOrganizationAiModel failed: ${error}`,
            );
            throw new Error(
                `!!! Failed to assign organization AI model: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion Organization AI Model

    // #region todoItems

    /** Les trois mutations renvoient la liste à jour : le dashboard n'affiche que le reste à faire. */
    private emitDashboardTodos(todoItems: TodoItemResponseDTO[]): void {
        if (!this.eventBus) return;

        this.eventBus.emit(
            "user-todos-updated",
            todoItems.filter((todo) => !todo.done).map(toDomainAcceptedTodo),
        );
    }

    /**
     * Récupère la liste des todo items de l'utilisateur courant
     */
    public async getUserTodoItems(): Promise<TodoItemResponseDTO[]> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeTodoItem;
        const urlParams = "";
        const bodyContent = undefined;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            return responseData as TodoItemResponseDTO[];
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getUserTodoItems failed: ${error}`,
            );
            throw new Error(
                `Failed to get user todo items: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /** Le todo existe déjà en base : son id suffit à l'identifier. */
    public async addTodoItemToDashboard(todoId: string): Promise<boolean> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.MeAddTodoItemToDashboard;
        const urlParams = "";
        const bodyContent = { todoId };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — todoId: ${todoId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            this.emitDashboardTodos(responseData as TodoItemResponseDTO[]);

            return true;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `addTodoItemToDashboard failed: ${error}`,
            );
            throw new Error(
                `Failed to add todo item to dashboard: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async setTodoItemState(
        todoId: string,
        isDone: boolean,
    ): Promise<boolean> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.MeSetTodoItemState;
        const urlParams = "";
        const bodyContent = { todoId, isDone };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — todoId: ${todoId} — isDone: ${isDone}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            this.emitDashboardTodos(responseData as TodoItemResponseDTO[]);

            return true;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `setTodoItemState failed: ${error}`,
            );
            throw new Error(
                `Failed to set todo item state: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    public async removeTodoItemFromDashboard(todoId: string): Promise<boolean> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.MeRemoveTodoItemFromDashboard;
        const urlParams = "";
        const bodyContent = { todoId };

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — todoId: ${todoId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
                bodyContent,
            );

            this.emitDashboardTodos(responseData as TodoItemResponseDTO[]);

            return true;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `removeTodoItemFromDashboard failed: ${error}`,
            );
            throw new Error(
                `Failed to remove todo item from dashboard: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion todoItems

    // #region User Signatures

    /**
     * Récupère toutes les signatures de l'utilisateur courant (GET v1/me/signatures).
     */
    public async getUserSignatures(): Promise<UserSignature[]> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeAllSignatures;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
            );

            return (responseData as GetUserSignaturesResponseDTO).map((dto) =>
                toDomainUserSignature(dto),
            );
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getUserSignatures failed: ${error}`,
            );
            throw new Error(
                `Failed to get user signatures: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Récupère une signature par son id (GET v1/me/signatures/:id).
     */
    public async getUserSignatureById(
        signatureId: string,
    ): Promise<UserSignature> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.MeSignatureById;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${signatureId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
                { id: signatureId },
            );

            return toDomainUserSignature(
                responseData as GetUserSignatureByIdResponseDTO,
            );
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getUserSignatureById failed: ${error}`,
            );
            throw new Error(
                `Failed to get user signature by id: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Crée une nouvelle signature (POST v1/me/signatures).
     */
    public async createUserSignature(
        userSignature: Omit<UserSignature, "id" | "userId" | "isFavorite">,
    ): Promise<UserSignature> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.MeSignature;
        const bodyContent: CreateUserSignatureRequestBody =
            toDtoUserSignatureInput(userSignature);

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
            );

            return toDomainUserSignature(
                responseData as CreateUserSignatureResponseDTO,
            );
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `createUserSignature failed: ${error}`,
            );
            throw new Error(
                `Failed to create user signature: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Met à jour une signature existante (PUT v1/me/signatures/:id).
     */
    public async updateUserSignature(
        userSignature: UserSignature,
    ): Promise<UserSignature> {
        const requestMethod = RequestMethod.PATCH;
        const endPoint = RestEndPoint.MeSignatureById;
        const bodyContent: UpdateUserSignatureRequestBody =
            toDtoUserSignatureUpdate(userSignature);

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${userSignature.id}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                bodyContent,
                { id: userSignature.id },
            );

            return toDomainUserSignature(
                responseData as UpdateUserSignatureResponseDTO,
            );
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `updateUserSignature failed: ${error}`,
            );
            throw new Error(
                `Failed to update user signature: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Supprime une signature (DELETE v1/me/signatures/:id).
     */
    public async deleteUserSignature(signatureId: string): Promise<boolean> {
        const requestMethod = RequestMethod.DELETE;
        const endPoint = RestEndPoint.MeSignatureById;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — id: ${signatureId}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                "",
                undefined,
                { id: signatureId },
            );

            return (responseData as DeleteUserSignatureResponseDTO).deleted;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `deleteUserSignature failed: ${error}`,
            );
            throw new Error(
                `Failed to delete user signature: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion User Signatures

    // #region Contacts

    /**
     * Récupère la photo de contact d'un utilisateur par son adresse email (GET /v1/contacts/photo?email=...).
     */
    public async getContactPhotoByEmail(email: string): Promise<string> {
        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.ContactPhoto;
        const urlParams = `?email=${encodeURIComponent(email)}`;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — email: ${email}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
            );

            return (responseData as GetContactPhotoResponseDTO).photo;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `getContactPhotoByEmail failed: ${error}`,
            );
            throw new Error(
                `Failed to get contact photo: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * Recherche hybride de contacts (GET /v1/contacts/search?data=...&top_k=...).
     *
     * Recherche sémantique + typo-tolérante côté backend (connecteur rag-contact).
     * `results` est déjà trié par pertinence décroissante par le serveur — ne pas re-trier.
     * L'enveloppe `{ data }` est déballée par `callApi`.
     *
     * @param query - Texte libre (nom, fragment d'email, langage naturel). Vide → court-circuit (aucun appel API).
     * @param topK  - Nombre max de résultats souhaités (clampé à [1, 50] pour éviter un 400 côté serveur).
     */
    public async searchContacts(
        query: string,
        topK: number = 10,
    ): Promise<SearchContactsResponseDTO> {
        const trimmedQuery = query.trim();

        // Court-circuit : `data` vide renverrait un 400 — inutile d'appeler l'API.
        if (trimmedQuery === "") {
            logger.info(
                [LOGS.ApiManager],
                `searchContacts: empty query, skipping API call`,
            );
            return { query: "", results: [], total: 0 };
        }

        const requestMethod = RequestMethod.GET;
        const endPoint = RestEndPoint.ContactSearch;

        // Clamp top_k à [1, 50] (au-delà ou non entier → 400 côté serveur).
        const clampedTopK = Math.min(Math.max(Math.trunc(topK), 1), 50);
        const urlParams = `?data=${encodeURIComponent(trimmedQuery)}&top_k=${clampedTopK}`;

        logger.info(
            [LOGS.ApiManager],
            `Calling Api: ${endPoint} — data: "${trimmedQuery}", top_k: ${clampedTopK}`,
        );

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
                urlParams,
            );

            return responseData as SearchContactsResponseDTO;
        } catch (error) {
            logger.error([LOGS.ApiManager], `searchContacts failed: ${error}`);
            throw new Error(
                `Failed to search contacts: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    /**
     * AB#1725 — Amorçage one-shot du carnet de contacts RAG (POST v1/contacts/rag-initialize).
     * ═══════════════════════════════════════════════════════════════════════════
     * Idempotent côté backend (flag de profil `hasContactRagBeenInitialized`) : déjà amorcé →
     * no-op serveur (`alreadyInitialized: true`, sans appel Graph/RAG). Déclenché par
     * ContactManager après la fin du démarrage (event `services-ready`) — remplace
     * l'ex-amorçage fire-and-forget du bootstrap backend.
     */
    public async initializeContactRag(): Promise<InitializeContactRagResponseDTO> {
        const requestMethod = RequestMethod.POST;
        const endPoint = RestEndPoint.ContactRagInitialize;

        logger.info([LOGS.ApiManager], `Calling Api: ${endPoint}`);

        try {
            const responseData = await this.callApi(
                [LOGS.ApiManager],
                requestMethod,
                endPoint,
            );

            return responseData as InitializeContactRagResponseDTO;
        } catch (error) {
            logger.error(
                [LOGS.ApiManager],
                `initializeContactRag failed: ${error}`,
            );
            throw new Error(
                `Failed to initialize contact RAG: ${error instanceof Error ? error.message : String(error)}`,
            );
        }
    }

    // #endregion Contacts

    // #endregion Public Methods

    // #region Private Methods
    private readonly methodsWithBody = new Set([
        RequestMethod.POST,
        RequestMethod.PUT,
        RequestMethod.PATCH,
    ]);

    /**
     * Résout les path params dans un endpoint (ex: `/organizations/:id/licenses/:orgLicenseId`).
     * @param endpoint - L'endpoint avec des placeholders `:param`
     * @param pathParams - Map des valeurs à substituer (ex: `{ id: 'abc', orgLicenseId: 'xyz' }`)
     */
    private _resolvePathParams(
        endpoint: RestEndPoint,
        pathParams?: Record<string, string>,
    ): string {
        if (!pathParams) return endpoint;
        return Object.entries(pathParams).reduce(
            (url, [key, value]) =>
                url.replace(`:${key}`, encodeURIComponent(value)),
            endpoint as string,
        );
    }

    /**
     * Champ `userTimezone` du corps d'une demande à l'assistant ou aux vues personnalisées (F-1.19) :
     * le fuseau IANA de l'appareil, lu à chaque appel. Omis s'il est inconnu, jamais `null` : l'Api-Ai
     * prend alors Paris par défaut. Rien n'est envoyé vers `/analysis`.
     */
    private _userTimezoneField(): { userTimezone?: string } {
        const userTimezone = getUserTimezone(this.readUserTimezone);
        return userTimezone ? { userTimezone } : {};
    }

    /**
     * Champs lus par l'Api-Backend seulement, pour ranger la consommation d'un appel de l'assistant :
     * sa conversation et son tour (le web n'envoie pas l'en-tête `X-Mv-Trace-Id`). Jamais relayés à
     * l'Api-Ai.
     */
    private _usageFields(options?: {
        conversationId?: string;
        traceId?: string;
    }): { conversationId?: string; turnId?: string } {
        return {
            ...(options?.conversationId
                ? { conversationId: options.conversationId }
                : {}),
            ...(options?.traceId ? { turnId: options.traceId } : {}),
        };
    }

    /**
     * Parse le body JSON d'une réponse en erreur de façon sûre.
     * Retourne { code, message } vides si le body est absent ou non parsable.
     */
    private async _parseErrorBody(
        response: Response,
    ): Promise<{ code: string; message: string }> {
        try {
            const errorBody = await response.json();
            return {
                code: errorBody?.code || "",
                message: errorBody?.message || "",
            };
        } catch {
            return { code: "", message: "" };
        }
    }

    /**
     * Construit l'erreur correspondant au status HTTP d'une réponse en erreur.
     */
    private async _buildHttpError(
        response: Response,
        endpoint: RestEndPoint,
    ): Promise<Error> {
        const status = response.status;
        const prefix = `callApi ${endpoint} failed with ${status}`;

        const functional = (description: string, code: string): Error =>
            code
                ? new ApiFunctionalError(description, status, code)
                : new Error(description);

        switch (status) {
            case 400: {
                const { code, message } = await this._parseErrorBody(response);
                const suffix = message ? ` — ${message}` : "";
                return functional(
                    `${prefix} : ${code || "bad request"}${suffix}`,
                    code,
                );
            }
            case 401:
                return new Error(`${prefix} : authentication rejected by API`);
            case 403: {
                const { code } = await this._parseErrorBody(response);
                return functional(`${prefix} : ${code || "forbidden"}`, code);
            }
            case ClientUpdateRequiredError.STATUS: {
                // 426 : version de l'app refusée par l'Api-Backend (F-1.25, BK-5).
                const { code } = await this._parseErrorBody(response);
                return new ClientUpdateRequiredError(
                    `${prefix} : ${code || "update required"}`,
                    code,
                );
            }
            case 502:
            case 503:
            case 504:
                // Panne passagère derrière l'Api-Backend : statut gardé, l'assistant rejoue l'appel une
                // fois (turnError.isReplayableCallError). Avant : une erreur sans statut, jamais rejouée
                // (audit de performance du 15/10, P3-08).
                return new ApiFunctionalError(
                    `${prefix} : service unavailable`,
                    status,
                    "",
                );
            case 429: {
                // Statut gardé même sans code : l'assistant y lit un quota épuisé (F-1.17).
                const { code } = await this._parseErrorBody(response);
                return new ApiFunctionalError(
                    `${prefix} : ${code || "too many requests"}`,
                    status,
                    code,
                );
            }
            case 500:
                return new Error(`${prefix} : ...`);
            default:
                return new Error(
                    `callApi ${endpoint} failed with unexpected error code ${status} !!!`,
                );
        }
    }

    /**
     * @param traceId - identifiant du tour de l'assistant (F-1.24), envoyé en `X-Mv-Trace-Id`
     * seulement si l'app a fourni `clientInfo`.
     */
    private async callApi(
        logCategory: LOGS[],
        method: RequestMethod,
        endpoint: RestEndPoint,
        urlParams?: string,
        bodyContent?: unknown,
        pathParams?: Record<string, string>,
        cachePolicy?: RequestCache,
        traceId?: string,
        signal?: AbortSignal,
    ): Promise<any> {
        // Build request url
        const resolvedEndpoint = this._resolvePathParams(endpoint, pathParams);
        const requestUrl = `${this.apiUrl}${this.apiVersion}${resolvedEndpoint}${urlParams ?? ""}`;

        // Build body
        const hasBody = this.methodsWithBody.has(method);
        if (bodyContent !== undefined && !hasBody) {
            logger.warn(
                [LOGS.ApiManager, ...logCategory],
                `Body ignored for ${method} request on ${endpoint}`,
            );
        }
        const requestBody =
            hasBody && bodyContent !== undefined
                ? JSON.stringify(bodyContent)
                : undefined;

        // Execute call
        try {
            let response: Response = await this._sendRequest(
                requestUrl,
                method,
                requestBody,
                cachePolicy,
                traceId,
                signal,
            );

            if (response.status === 401) {
                logger.warn(
                    [LOGS.ApiManager, ...logCategory],
                    `401 on ${endpoint}, forcing token refresh and retrying`,
                );
                await this.authContext.forceRefresh();
                response = await this._sendRequest(
                    requestUrl,
                    method,
                    requestBody,
                    cachePolicy,
                    traceId,
                    signal,
                );

                if (response.status === 401) {
                    logger.error(
                        [LOGS.ApiManager, ...logCategory],
                        `Second 401 on ${endpoint} after retry — session rejected`,
                    );
                    this.eventBus.emit("auth-token-rejected", {
                        source: "ApiManager.callApi",
                    });
                }
            }

            // Check if response is not OK
            if (!response.ok) {
                logger.info(
                    LOGS.ApiManager,
                    `!!! callApi ${endpoint} response indicates error`,
                    response.statusText,
                );

                const httpError = await this._buildHttpError(
                    response,
                    endpoint,
                );

                logger.error(LOGS.ApiManager, httpError.message);

                // Version refusée (F-1.25) : l'app mobile affiche l'écran « mise à jour requise ».
                if (httpError instanceof ClientUpdateRequiredError) {
                    this.eventBus.emit("client-update-required");
                }

                throw httpError;
            }

            // Response is OK, extract and return responseData
            const result = (await response.json()) as MailvistaResponseBody;

            if (!result) {
                throw new Error(
                    "Unable to extract correct MailvistaResponseBody content from response",
                );
            }
            if (!result.data) {
                throw new Error(
                    `!!! callApi ${endpoint} response content is empty`,
                );
            }

            logger.info(LOGS.ApiManager, `    ${endpoint} result`, result);

            return result.data;
        } catch (error) {
            logger.info(
                [LOGS.ApiManager, ...logCategory],
                `  => callApi catching error`,
                error,
            );

            const errorMessage = `Error in callApi for operation ${endpoint}: ${error?.message}`;
            logger.error([LOGS.ApiManager, ...logCategory], errorMessage);

            if (error instanceof ApiFunctionalError) {
                throw error;
            }

            // L'erreur d'origine reste en `cause` (réseau coupé, par exemple — F-1.17).
            throw errorWithCause(errorMessage, error);
        }
    }

    private async _sendRequest(
        requestUrl: string,
        method: RequestMethod,
        body: string | undefined,
        cache?: RequestCache,
        turnTraceId?: string,
        signal?: AbortSignal,
    ): Promise<Response> {
        const accessToken = await this.authContext.getAccessToken();
        const providerToken = await this.authContext.getProviderToken();

        const headers: Record<string, string> = {
            "Content-Type": "application/json; charset=utf-8",
            Authorization: `Bearer ${accessToken}`,
            "X-Provider-Token": providerToken,
        };

        // Identification du client (F-1.24) : seulement si l'app a fourni `clientInfo`.
        if (this.clientHeader) {
            headers["X-Mv-Client"] = this.clientHeader;
        }
        // Identifiant du tour de l'assistant, avec `clientInfo` seulement.
        if (this.clientHeader && turnTraceId) {
            headers["X-Mv-Trace-Id"] = turnTraceId;
        }

        try {
            return await fetch(requestUrl, {
                method,
                headers,
                body,
                cache,
                // Tour de l'assistant annulé : la requête en vol est abandonnée.
                signal,
            });
        } catch (error) {
            // Aucune réponse HTTP : marquée comme erreur réseau (cause d'un tour raté, F-1.17).
            throw new ApiNetworkError(
                error instanceof Error ? error.message : String(error),
                { cause: error },
            );
        }
    }

    // #endregion Private Methods
}
